import { graphql } from 'src/graphql';
import { withItem, withoutItem } from 'src/network/cache/listItems';

import { ManagedTagFields, ManagedTags } from './ManagedTags.query';
import { TeamTags } from './TeamTags.query';

import type { ManagedTag } from './ManagedTags.query';
import type { TeamTag } from './TeamTags.query';
import type { Cache } from '@urql/exchange-graphcache';

const TagValues = graphql(`
  fragment TagValues on TagType {
    _id
    name
    color
    description
  }
`);

const HighlightTagIds = graphql(`
  fragment HighlightTagIds on HighlightType {
    _id
    tag_ids
  }
`);

const tagKey = (cache: Cache, id: string) => cache.keyOfEntity({ __typename: 'TagType', _id: id });

/** The links of a list field, or the strings of a scalar list such as `tag_ids`. */
const linkedKeys = (cache: Cache, entity: string, field: string) => {
  const value = cache.resolve(entity, field);
  return Array.isArray(value) ? value.filter((key): key is string => typeof key === 'string') : [];
};

const queryFieldKeys = (cache: Cache, fieldName: string) =>
  cache
    .inspectFields('Query')
    .filter(field => field.fieldName === fieldName)
    .map(field => field.fieldKey);

/** Every cached tag list, whatever its variables, so the transcript and the settings lists stay in step. */
const removeFromTagLists = (cache: Cache, id: string) => {
  const removed = tagKey(cache, id);
  queryFieldKeys(cache, 'tags').forEach(fieldKey => {
    const connection = cache.resolve('Query', fieldKey);
    if (typeof connection === 'string') {
      cache.link(
        connection,
        'nodes',
        linkedKeys(cache, connection, 'nodes').filter(key => key !== removed),
      );
    }
  });
};

/** Rewrites the tags of each cached transcript highlight. `null` drops the highlight from its list. */
const updateRoomHighlights = (cache: Cache, update: (tagIds: readonly string[]) => string[] | null) => {
  queryFieldKeys(cache, 'playbackGetHighlights').forEach(fieldKey => {
    const kept = linkedKeys(cache, 'Query', fieldKey).flatMap(highlightKey => {
      const _id = cache.resolve(highlightKey, '_id');
      const tagIds = linkedKeys(cache, highlightKey, 'tag_ids');
      const next = update(tagIds);
      if (!next) {
        return [];
      }
      if (typeof _id === 'string') {
        cache.writeFragment(HighlightTagIds, { _id, tag_ids: next });
      }
      const tags = cache.resolve(highlightKey, 'tags');
      if (typeof tags === 'string') {
        cache.link(
          tags,
          'nodes',
          next.flatMap(tagId => tagKey(cache, tagId) ?? []),
        );
      }
      return [highlightKey];
    });
    cache.link('Query', fieldKey, kept);
  });
};

export const updateTagInCache = (
  cache: Cache,
  {
    id,
    name,
    color,
    description,
  }: { id: string; name?: string | null; color?: string | null; description?: string | null },
) => {
  const tag = cache.readFragment(TagValues, `TagType:${id}`);
  if (tag) {
    cache.writeFragment(TagValues, {
      ...tag,
      name: name ?? tag.name,
      color: color ?? tag.color,
      description: description ?? tag.description,
    });
  }
};

/** The API deletes each highlight that had only this tag, and takes the tag off the others. */
export const deleteTagFromCache = (cache: Cache, id: string) => {
  removeFromTagLists(cache, id);
  updateRoomHighlights(cache, tagIds => {
    if (!tagIds.includes(id)) {
      return [...tagIds];
    }
    return tagIds.length === 1 ? null : tagIds.filter(tagId => tagId !== id);
  });
};

export const mergeTagInCache = (cache: Cache, targetId: string, destinationId: string) => {
  const target = cache.readFragment(ManagedTagFields, `TagType:${targetId}`);
  const destination = cache.readFragment(ManagedTagFields, `TagType:${destinationId}`);
  if (target && destination) {
    cache.writeFragment(ManagedTagFields, {
      ...destination,
      highlight_ids: [...new Set([...destination.highlight_ids, ...target.highlight_ids])],
    });
  }
  removeFromTagLists(cache, targetId);
  updateRoomHighlights(cache, tagIds =>
    tagIds.includes(targetId)
      ? [...new Set(tagIds.map(tagId => (tagId === targetId ? destinationId : tagId)))]
      : [...tagIds],
  );
};

export const addTagToManagedTags = (cache: Cache, teamId: string, tag: ManagedTag) =>
  cache.updateQuery({ query: ManagedTags, variables: { teamId } }, data =>
    data && !data.tags.nodes?.some(node => node?._id === tag._id)
      ? { tags: { ...data.tags, nodes: [...(data.tags.nodes ?? []), tag] } }
      : data,
  );

/** `TeamTags` sorts the most recently used first, so a new tag goes first. */
export const addTagToCache = (cache: Cache, teamId: string, tag: TeamTag) =>
  cache.updateQuery({ query: TeamTags, variables: { teamId } }, data =>
    data ? { tags: { ...data.tags, nodes: withItem(data.tags.nodes, tag, true) } } : null,
  );

export const removeTagFromCache = (cache: Cache, teamId: string, id: string) =>
  cache.updateQuery({ query: TeamTags, variables: { teamId } }, data =>
    data ? { tags: { ...data.tags, nodes: withoutItem(data.tags.nodes, id) } } : null,
  );
